import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Scale, Globe, Users, ArrowRight, Gavel, DollarSign, Car, Home, Heart, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import LanguageSelector from '../components/LanguageSelector';
import { Helmet } from 'react-helmet';

const LandingPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const isRTL = language === 'ar' || language === 'ur';

  const services = [
    {
      icon: Globe,
      title: t('services.immigration'),
      description: t('services.immigrationDesc')
    },
    {
      icon: Gavel,
      title: t('services.criminal'),
      description: t('services.criminalDesc')
    },
    {
      icon: DollarSign,
      title: t('services.debt'),
      description: t('services.debtDesc')
    },
    {
      icon: Car,
      title: t('services.traffic'),
      description: t('services.trafficDesc')
    },
    {
      icon: Scale,
      title: t('services.inheritance'),
      description: t('services.inheritanceDesc')
    },
    {
      icon: Home,
      title: t('services.rental'),
      description: t('services.rentalDesc')
    },
    {
      icon: Heart,
      title: t('services.family'),
      description: t('services.familyDesc')
    },
    {
      icon: Users,
      title: t('services.support'),
      description: t('services.supportDesc')
    }
  ];

  return (
    <div className={`min-h-screen ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <nav className="fixed top-0 w-full z-50 glass border-b border-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_legal-booking-hub/artifacts/evfhhmo1_logo%20studio%20legale_edited_edited.avif" 
                alt="Studio Legale"
                className="h-16 w-16 object-contain"
                style={{ imageRendering: 'crisp-edges' }}
              />
              <span className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Studio Legale
              </span>
            </div>
            <div className="flex items-center space-x-6">
              <LanguageSelector />
              <Button
                onClick={() => navigate('/booking')}
                className="bg-secondary text-primary hover:bg-secondary/90 rounded-sm px-6 py-2"
                data-testid="nav-book-button"
              >
                Prenota Consulenza
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <section className="relative h-screen flex flex-col overflow-hidden">
        <div className="flex-1 grid grid-cols-2">
          <div 
            className="bg-cover bg-center relative"
            style={{
              backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/1932d206-0b7b-48bc-9055-c72bf2a29e8c/images/6ca4abec6e2c5e352b592f37e4306dbec9ed911828a86e1a20ad2af95ee022b8.png')`
            }}
          >
            <div className="absolute inset-0 bg-primary/40"></div>
          </div>
          <div 
            className="bg-cover bg-center relative"
            style={{
              backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/1932d206-0b7b-48bc-9055-c72bf2a29e8c/images/839fd223808bff1d7e33a96c1624d327d12252395a76c5916f1eefe9e2a748ae.png')`
            }}
          >
            <div className="absolute inset-0 bg-primary/40"></div>
          </div>
        </div>
        <div className="bg-primary relative z-10 py-16 px-4 sm:px-6 lg:px-8 noise-texture text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6 text-white">
              {t('hero.title')}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <Button
                onClick={() => navigate('/booking')}
                className="bg-secondary text-primary hover:bg-secondary/90 rounded-sm px-8 py-6 text-lg font-medium shadow-xl hover:shadow-2xl transition-all"
                data-testid="hero-book-button"
              >
                Prenota Consulenza <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center bg-white/20 backdrop-blur-md px-8 py-4 rounded-lg mx-auto inline-flex">
              <span className="text-white font-semibold text-lg">{t('hero.phone')}</span>
              <span className="hidden sm:inline text-secondary text-xl">•</span>
              <span className="text-white font-semibold text-lg">{t('hero.address')}</span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">
              {t('services.title')}
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-white border border-border hover:border-secondary/50 transition-all duration-300 shadow-sm hover:shadow-md rounded-sm p-8"
                  data-testid={`service-card-${index}`}
                >
                  <Icon className="h-12 w-12 text-secondary mb-6" strokeWidth={1.5} />
                  <h3 className="text-2xl md:text-3xl font-medium mb-4">{service.title}</h3>
                  <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
                    {service.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 noise-texture" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <img
              src="https://static.wixstatic.com/media/e84d49_1ca30592304845509b4473a9182ae8fc~mv2.jpg/v1/fill/w_477,h_613,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/foto%20primo%20piano_jfif.jpg"
              alt="Avvocato Nada Zhiti - Studio Legale Casale Monferrato"
              className="w-48 h-48 rounded-sm mx-auto mb-8 object-cover border-4 border-secondary shadow-xl"
            />
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Avvocato Nada Zhiti</h2>
            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Consulenze in italiano e albanese, con supporto multilingua.
            </p>
            <Button
              onClick={() => navigate('/booking')}
              className="bg-secondary text-primary hover:bg-secondary/90 rounded-sm px-8 py-6 text-lg font-medium shadow-xl"
              data-testid="footer-cta-button"
            >
              Prenota Consulenza <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Contact Section - SEO Optimized */}
      <section className="py-16 bg-white" id="contatti">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-primary" style={{ fontFamily: 'Playfair Display, serif' }}>
            Contattaci
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-lg bg-gray-50">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-secondary" />
              <h3 className="text-xl font-semibold mb-2 text-primary">Indirizzo</h3>
              <p className="text-gray-600">Via Roma, 24</p>
              <p className="text-gray-600">15033 Casale Monferrato (AL)</p>
              <p className="text-gray-600">Piemonte, Italia</p>
            </div>
            <div className="text-center p-6 rounded-lg bg-gray-50">
              <Phone className="h-12 w-12 mx-auto mb-4 text-secondary" />
              <h3 className="text-xl font-semibold mb-2 text-primary">Telefono</h3>
              <a href="tel:+393462393411" className="text-gray-600 hover:text-secondary text-lg">
                +39 346 239 3411
              </a>
              <p className="text-gray-500 text-sm mt-2">Chiamaci per informazioni</p>
            </div>
            <div className="text-center p-6 rounded-lg bg-gray-50">
              <Clock className="h-12 w-12 mx-auto mb-4 text-secondary" />
              <h3 className="text-xl font-semibold mb-2 text-primary">Orari</h3>
              <p className="text-gray-600">Lunedì - Venerdì</p>
              <p className="text-gray-600">09:00 - 18:00</p>
              <p className="text-gray-500 text-sm mt-2">Su appuntamento</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-primary text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="text-lg font-semibold mb-4 text-secondary">Studio Legale</h4>
              <p className="text-gray-300 text-sm">
                Avvocato Nada Zhiti offre consulenze legali professionali in diritto civile, immigrazione e diritto di famiglia.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4 text-secondary">Servizi</h4>
              <ul className="text-gray-300 text-sm space-y-2">
                <li>Diritto dell&apos;Immigrazione</li>
                <li>Diritto Civile</li>
                <li>Diritto di Famiglia</li>
                <li>Recupero Crediti</li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4 text-secondary">Contatti</h4>
              <ul className="text-gray-300 text-sm space-y-2">
                <li>Via Roma, 24 - Casale Monferrato</li>
                <li>Tel: +39 346 239 3411</li>
                <li>Email: zhitinada@gmail.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="text-sm text-gray-400">
              © 2026 Studio Legale Avvocato Nada Zhiti. Tutti i diritti riservati. | P.IVA: 02680260029
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Avvocato iscritto all&apos;Ordine degli Avvocati di Vercelli
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;